/**
 * Customer API Client
 *
 * This utility provides functions for interacting with the NameBubbles Customer API
 * to fetch partner products and manage cart items.
 */

// API base URL - should come from environment variables
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Partner information returned from API
 */
export interface PartnerInfo {
  partner_id: string;
  name: string;
  logo_url?: string;
  refersion_id?: string;
}

/**
 * Product URL from catalog response
 */
export interface ProductUrl {
  tag: string;
  url: string;
}

/**
 * Product catalog response
 */
export interface ProductCatalogResponse {
  products: ProductUrl[];
  partner?: PartnerInfo;
}

/**
 * Full product data fetched from presigned URL
 */
export interface ProductData {
  product_id: string;
  tag: string;
  title: string;
  description?: string;
  shopify_product_id: string;
  shopify_variant_id: string;
  price_cents: number;
  thumbnail_url?: string;
  styles: ProductStyle[];
  text_fields: ProductTextField[];
  palettes: ProductPalette[];
  // Partner-specific fields
  partner_id?: string;
  partner_product_id?: string;
}

export interface ProductStyle {
  style_id: string;
  name: string;
  thumbnail_url?: string;
}

export interface ProductTextField {
  text_field_id: string;
  name: string;
  placeholder?: string;
  max_length?: number;
  required: boolean;
}

export interface ProductPalette {
  palette_id: string;
  name: string;
  colors: string[];
}

/**
 * API Error
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Fetch partner product catalog
 * This also implicitly validates the partner - returns 404 if partner not found/inactive
 */
export async function fetchPartnerCatalog(
  partnerId?: string,
  refersionId?: string
): Promise<ProductCatalogResponse> {
  const params = new URLSearchParams();

  if (partnerId) {
    params.set('partner_id', partnerId);
  }
  if (refersionId) {
    params.set('refersion_id', refersionId);
  }

  const response = await fetch(`${API_BASE_URL}/products?${params.toString()}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 404) {
      throw new ApiError(
        errorData.message || 'Partner not found',
        404,
        'PARTNER_NOT_FOUND'
      );
    }

    throw new ApiError(
      errorData.message || 'Failed to fetch product catalog',
      response.status
    );
  }

  return response.json();
}

/**
 * Fetch full product data from presigned URL
 */
export async function fetchProductData(url: string): Promise<ProductData> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new ApiError('Failed to fetch product data', response.status);
  }

  return response.json();
}

/**
 * Fetch a single product by tag
 */
export async function fetchProduct(
  productTag: string,
  partnerId?: string,
  refersionId?: string
): Promise<{ url: string }> {
  const params = new URLSearchParams();

  if (partnerId) {
    params.set('partner_id', partnerId);
  }
  if (refersionId) {
    params.set('refersion_id', refersionId);
  }

  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE_URL}/products/${productTag}?${queryString}`
    : `${API_BASE_URL}/products/${productTag}`;

  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 404) {
      throw new ApiError(
        errorData.message || 'Product not found',
        404,
        'PRODUCT_NOT_FOUND'
      );
    }

    throw new ApiError(
      errorData.message || 'Failed to fetch product',
      response.status
    );
  }

  return response.json();
}

// ============================================================================
// Shopify Product Display Data API
// ============================================================================

/**
 * Shopify product display info
 */
export interface ShopifyProductInfo {
  shopify_product_id: string;
  shopify_variant_id: string;
  title: string;
  description: string | null;
  price_cents: number;
  thumbnail_url: string | null;
  tag: string;
}

/**
 * Shopify products response
 */
export interface ShopifyProductsResponse {
  products: Record<string, ShopifyProductInfo>;
}

/**
 * Fetch product display data from Shopify by configurator tags
 *
 * @param tags - Array of configurator product tags
 * @returns Map of tag to Shopify product info
 */
export async function fetchShopifyProductInfo(
  tags: string[]
): Promise<Record<string, ShopifyProductInfo>> {
  if (tags.length === 0) {
    return {};
  }

  const response = await fetch(
    `${API_BASE_URL}/shopify-products?tags=${tags.join(',')}`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to fetch Shopify product info',
      response.status
    );
  }

  const data: ShopifyProductsResponse = await response.json();
  return data.products;
}

/**
 * Cart item creation request
 */
export interface CreateCartItemRequest {
  product_id: string;
  style_id: string;
  palette_id?: string;
  font_id?: string;
  icon_id?: string;
  text_fields: Array<{ text_field_id: string; value: string }>;
  partner_id?: string;
}

/**
 * Cart item response
 */
export interface CartItemResponse {
  cart_item_id: string;
  product_id: string;
  style_id: string;
  thumbnail_url?: string;
}

/**
 * Create a cart item
 */
export async function createCartItem(
  data: CreateCartItemRequest
): Promise<CartItemResponse> {
  const response = await fetch(`${API_BASE_URL}/cart-items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to create cart item',
      response.status
    );
  }

  return response.json();
}

/**
 * Get a cart item by ID
 */
export async function getCartItem(
  cartItemId: string
): Promise<CartItemResponse> {
  const response = await fetch(`${API_BASE_URL}/cart-items/${cartItemId}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError('Cart item not found', 404, 'CART_ITEM_NOT_FOUND');
    }
    throw new ApiError('Failed to fetch cart item', response.status);
  }

  return response.json();
}

// ============================================================================
// Shopify Cart API (proxied through Customer API)
// ============================================================================

/**
 * Cart attribute (key-value pair)
 */
export interface CartAttribute {
  key: string;
  value: string;
}

/**
 * Cart line item
 */
export interface CartLineItem {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    price: {
      amount: string;
      currencyCode: string;
    };
    product: {
      title: string;
      featuredImage?: {
        url: string;
        altText?: string;
      };
    };
  };
  attributes: CartAttribute[];
}

/**
 * Cart cost breakdown
 */
export interface CartCost {
  subtotalAmount: {
    amount: string;
    currencyCode: string;
  };
  totalAmount: {
    amount: string;
    currencyCode: string;
  };
}

/**
 * Full cart object
 */
export interface Cart {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  cost: CartCost;
  lines: {
    edges: Array<{
      node: CartLineItem;
    }>;
  };
  attributes: CartAttribute[];
}

/**
 * Create a new Shopify cart
 */
export async function createShopifyCart(
  attributes?: CartAttribute[]
): Promise<{ cartId: string; checkoutUrl: string; cart: Cart }> {
  const response = await fetch(`${API_BASE_URL}/cart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to create cart',
      response.status
    );
  }

  return response.json();
}

/**
 * Get Shopify cart by ID
 */
export async function getShopifyCart(cartId: string): Promise<Cart | null> {
  const response = await fetch(
    `${API_BASE_URL}/cart/${encodeURIComponent(cartId)}`
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to get cart',
      response.status
    );
  }

  const data = await response.json();
  return data.cart;
}

/**
 * Add item to Shopify cart
 */
export async function addToShopifyCart(
  cartId: string,
  variantId: string,
  quantity: number,
  attributes?: CartAttribute[]
): Promise<Cart> {
  const response = await fetch(
    `${API_BASE_URL}/cart/${encodeURIComponent(cartId)}/items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantId, quantity, attributes })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to add to cart',
      response.status
    );
  }

  const data = await response.json();
  return data.cart;
}

/**
 * Update cart line item quantity
 */
export async function updateShopifyCartItem(
  cartId: string,
  lineId: string,
  quantity: number
): Promise<Cart> {
  const response = await fetch(
    `${API_BASE_URL}/cart/${encodeURIComponent(
      cartId
    )}/items/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to update cart item',
      response.status
    );
  }

  const data = await response.json();
  return data.cart;
}

/**
 * Remove item from Shopify cart
 */
export async function removeFromShopifyCart(
  cartId: string,
  lineId: string
): Promise<Cart> {
  const response = await fetch(
    `${API_BASE_URL}/cart/${encodeURIComponent(
      cartId
    )}/items/${encodeURIComponent(lineId)}`,
    {
      method: 'DELETE'
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to remove from cart',
      response.status
    );
  }

  const data = await response.json();
  return data.cart;
}

/**
 * Update Shopify cart attributes
 */
export async function updateShopifyCartAttributes(
  cartId: string,
  attributes: CartAttribute[]
): Promise<Cart> {
  const response = await fetch(
    `${API_BASE_URL}/cart/${encodeURIComponent(cartId)}/attributes`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || 'Failed to update cart attributes',
      response.status
    );
  }

  const data = await response.json();
  return data.cart;
}

/**
 * Helper to extract line items from cart
 */
export function getCartLineItems(cart: Cart): CartLineItem[] {
  return cart.lines.edges.map(edge => edge.node);
}
